export enum CreditDecisionProductTypeEnum {
  SALARY_TOPUP = "SALARY_TOPUP",
  EWA = "EWA",
}

export enum CreditDecisionPurposeEnum {
  SALARY_TOPUP_PRE_APPLY = "SALARY_TOPUP_PRE_APPLY",
  SALARY_TOPUP_POST_APPLY = "SALARY_TOPUP_POST_APPLY",
  SALARY_TOPUP_MULTI_TERM_PRICING = "SALARY_TOPUP_MULTI_TERM_PRICING",
  EWA_WITHDRAWAL_INFO = "EWA_WITHDRAWAL_INFO",
  EWA_PRICING = "EWA_PRICING",
}

export enum InterestRateTypeEnum {
  REDUCING_BALANCE_RATE = "REDUCING_BALANCE_RATE",
  FLAT_BALANCE_RATE = "FLAT_BALANCE_RATE",
}

export enum ProcessingFeeTypeEnum {
  PERCENTAGE = "PERCENTAGE",
  PERCENTAGE_MONTHLY = "PERCENTAGE_MONTHLY",
  FIXED = "FIXED",
}

export enum RafikiDecisionTreeModelEnum {
  POS_WITHOUT_EWA_2024_06_19 = "POS_WITHOUT_EWA_2024_06_19",
  POS_WITH_EWA_2024_06_19 = "POS_WITH_EWA_2024_06_19",
  REBEL_POS_WITHOUT_EWA_20240729 = "REBEL_POS_WITHOUT_EWA_20240729",
  REBEL_POS_WITH_EWA_20240729 = "REBEL_POS_WITH_EWA_20240729",
  SMS_POS_WITH_EWA_20240729 = "SMS_POS_WITH_EWA_20240729",
  SMS_POS_WITHOUT_EWA_20240729 = "SMS_POS_WITHOUT_EWA_20240729",
  SODEXO_POS_WITHOUT_EWA_20240729 = "SODEXO_POS_WITHOUT_EWA_20240729",
  SODEXO_POS_WITH_EWA_20240729 = "SODEXO_POS_WITH_EWA_20240729",
  METRO_POS_WITH_EWA_20240729 = "METRO_POS_WITH_EWA_20240729",
  METRO_POS_WITHOUT_EWA_20240729 = "METRO_POS_WITHOUT_EWA_20240729",
  ECOM_POS_WITH_EWA_20240729 = "ECOM_POS_WITH_EWA_20240729",
  ECOM_POS_WITHOUT_EWA_20240729 = "ECOM_POS_WITHOUT_EWA_20240729",
  IPROCESS_POS_WITH_EWA_20240729 = "IPROCESS_POS_WITH_EWA_20240729",
  IPROCESS_POS_WITHOUT_EWA_20240729 = "IPROCESS_POS_WITHOUT_EWA_20240729",
  PROBABILITY_OF_DEFAULT_GOVERNMENT_OF_RAJASTHAN = "PROBABILITY_OF_DEFAULT_GOVERNMENT_OF_RAJASTHAN",
  PROBABILITY_OF_DEFAULT_CVENT = "PROBABILITY_OF_DEFAULT_CVENT",
  PROBABILITY_OF_DEFAULT_V1_B3 = "PROBABILITY_OF_DEFAULT_V1_B3",
  PROBABILITY_OF_DEFAULT_V1_B24 = "PROBABILITY_OF_DEFAULT_V1_B24",
  PROBABILITY_OF_DEFAULT_B2B_EMPLOYEE = "PROBABILITY_OF_DEFAULT_B2B_EMPLOYEE",
}

// --- Audit Log ---
export interface AuditLogClientReferences {
  clientUserId: string;
  clientWorkerId: string;
  clientEmployerId: string;
}

export interface CreditDecisionAuditLog {
  _id: string;
  productType: CreditDecisionProductTypeEnum;
  purpose: CreditDecisionPurposeEnum;
  userId?: string;
  workerId?: string;
  employerId?: string;
  clientApplicationId?: string;
  clientReferences: AuditLogClientReferences;
  approved: boolean;
  evaluatedAt: string;
  requestId?: string;
  contentHash?: string;
  requestSnapshot: Record<string, any>;
  responseSnapshot: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

// --- Manual Override ---
export interface ManualOverrideUpdatedTerms {
  amount?: number;
  tenureInMonths?: number;
  irpa?: number;
  pf?: number;
}

export interface ManualOverrideRequest {
  applicationId: string;
  status: "IN_PROGRESS" | "REJECTED";
  reasons: string[];
  updatedTerms?: ManualOverrideUpdatedTerms;
}

// --- Rafiki User Features (responseSnapshot.salaryTopup.userFeatures) ---
export interface RafikiUserFeatures {
  EMPLOYEE_MONTHLY_GROSS_SALARY?: number | null;
  EMPLOYEE_MONTHLY_SALARY?: number | null;
  USER_MONTHLY_SANCTIONABLE_AMOUNT?: number | null;
  USER_MONTHLY_SANCTIONABLE_AMOUNT_FOR_TOPUP_LOAN?: number | null;
  USER_AGE_IN_YEARS?: number | null;
  NUMBER_OF_DAYS_LEFT_FOR_RETIREMENT?: number | null;
  IS_LATEST_SALARY_CREDIT_WITHIN_EXPECTED_DATE?: boolean | null;
  NO_OF_DAYS_SINCE_LAST_SALARY_CREDIT?: number | null;
  EQUIFAX_CREDIT_SCORE?: number | null;
  CURRENT_FOIR_OBLIGATION_AMOUNT_V4?: number | null;
  ACTIVE_SALARY_TOPUP_COUNT?: number | null;
  EWA_COUNT_TOTAL?: number | null;
  EWA_OS_BALANCE?: number | null;
  OUTSTANDING_AMOUNT_FOR_LATEST_ACTIVE_APPLICATION?: number | null;
  LATEST_ACTIVE_SALARY_TOPUP_PAID_INSTALMENT_COUNT?: number | null;
  LATEST_ACTIVE_SALARY_TOPUP_DPD?: number | null;
  DPD30_LAST_36M_0_3M_OUTSTANDING_AMOUNT?: number | null;
  DPD90_LAST_36M_0_6M_OUTSTANDING_AMOUNT?: number | null;
  [key: string]: unknown;
}
export interface AuditLogListQuery {
  productType: CreditDecisionProductTypeEnum;
  purpose?: CreditDecisionPurposeEnum;
  approved?: string;
  userId?: string;
  workerId?: string;
  employerId?: string;
  clientApplicationId?: string;
  clientUserId?: string;
  clientWorkerId?: string;
  clientEmployerId?: string;
  requestId?: string;
  evaluatedAtFrom?: string;
  evaluatedAtTo?: string;
  cursor?: string;
  limit?: string;
}

export interface AuditLogListResponse {
  data: CreditDecisionAuditLog[];
  nextCursor?: string;
  hasMore: boolean;
}

// --- Pre-Approval ---
export interface PreApprovalRange {
  min: number;
  max: number;
}

export interface PreApprovalPricing {
  irpa: number;
  processingFee: number;
  processingFeeType: ProcessingFeeTypeEnum;
  interestRateType: InterestRateTypeEnum;
}

export enum PreApprovalActorTypeEnum {
  ADMIN = "ADMIN",
  LOAN_DISBURSED_CALLBACK = "LOAN_DISBURSED_CALLBACK",
}

export interface PreApproval {
  _id: string;
  lenderId: string;
  clientId: string;
  workerId: string;
  employerId: string;
  amount: PreApprovalRange;
  tenureInMonths: PreApprovalRange;
  pricing: PreApprovalPricing;
  policies?: string[];
  validFrom: string;
  validUntil: string;
  createdBy: string;
  remarks?: string;
  expiredAt?: string;
  expiredBy?: string;
  expiryReason?: string;
  expiryActor?: PreApprovalActorTypeEnum;
  expirySourceRefId?: string;
}

export interface CreatePreApprovalRequest {
  workerId: string;
  employerId: string;
  amount: PreApprovalRange;
  tenureInMonths: PreApprovalRange;
  pricing: PreApprovalPricing;
  policies?: string[];
  validFrom?: string;
  validUntil: string;
  remarks?: string;
}

export interface ExpirePreApprovalRequest {
  expiredAt?: string;
  reason?: string;
  sourceRefId?: string;
}

export interface ExpirePreApprovalResponse {
  expired: boolean;
  preApprovalId?: string;
}

export interface PreApprovalListQuery {
  workerId?: string;
  employerId?: string;
  isActive?: boolean;
  limit?: number;
  cursor?: string;
}

export interface PreApprovalListResponse {
  data: PreApproval[];
  nextCursor?: string;
  hasMore: boolean;
}

// --- Pricing ---
export interface CreditDecisionPricingSlab {
  fromAmount: number;
  tillAmount: number;
  processingFeeWithGst: number;
  processingFeeType?: ProcessingFeeTypeEnum;
  irpa: number;
  interestType: InterestRateTypeEnum;
}

export interface CreditDecisionPricing {
  _id: string;
  lenderId: string;
  clientId: string;
  name: string;
  pricingSlabs: CreditDecisionPricingSlab[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreditDecisionPricingListResponse {
  data: CreditDecisionPricing[];
  nextCursor?: string;
  hasMore: boolean;
}

export interface CreateCreditDecisionPricingRequest {
  name: string;
  pricingSlabs: CreditDecisionPricingSlab[];
}

// --- Employer Config (update-config endpoint) ---
export interface EwaCreditDecisionConfig {
  isEnabled: boolean;
  maxWithdrawalPerTransaction?: number;
  maxWithdrawalPerDay?: number;
  maxWithdrawalAmountPerCycle?: number;
  maxTransactionsPerCycle?: number;
  maxTransactionsPerCalendarYear?: number;
  probationPeriodInDays?: number;
}

export interface PricingConfig {
  pricingId?: string;
}

export interface UpdateEmployerConfigRequest {
  maxWithdrawalPercentage?: number;
  minEmploymentDurationForLoanEligibility?: number;
  isDisbursalEnabled?: boolean;
  ewaCreditDecisionConfig?: EwaCreditDecisionConfig;
  pricingConfig?: PricingConfig;
  salaryTopupDecisionConfig?: SalaryTopupDecisionConfig;
}

// --- Salary Topup Decision Config (read-only in update-config) ---
export interface SalaryTopupDecisionRangeConfig {
  min?: number;
  max?: number;
}

export interface SalaryTopupDecisionLoanConstraintsConfig {
  amount?: SalaryTopupDecisionRangeConfig;
  tenureInMonths?: SalaryTopupDecisionRangeConfig;
  maxFoirThresholdPercentage?: number;
}

export interface SalaryTopupDecisionConfig {
  loanConstraints?: SalaryTopupDecisionLoanConstraintsConfig;
  maxAnnualPercentageRate?: number;
  mlModels?: string[];
  manualReview?: ManualReviewConfig;
}

// --- Manual Review Config (employer config) ---
export interface ManualReviewConfig {
  manualReviewRequired: boolean;
  applicabilityMetadata?: ApplicabilityMetadata;
}

export interface ApplicabilityMetadata {
  strategy?: "USER_ID_SUFFIX" | "SEGMENT" | "GEOLOCATION";
  userIdSuffixes: string[];
}
